import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, fmtDate, StatusBadge } from "@/components/shared";
import { bandColor } from "./Reports";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { RadialBarChart, RadialBar, PolarAngleAxis, ResponsiveContainer } from "recharts";
import { Download, ArrowLeft, Lock, ChevronDown } from "lucide-react";

export default function ReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/reports/${id}`).then((r) => setData(r.data)).catch(() => setData(false)); }, [id]);
  if (data === null) return <Loader label="Loading report" />;
  if (data === false) return <div className="text-sm text-muted-foreground">Report not available.</div>;

  const { submission, scores, validation, detail_level } = data;
  const vcomments = {};
  (validation?.question_comments || []).forEach((c) => { vcomments[c.question_id] = c; });

  const download = (fmt) => {
    window.open(`${API}/reports/${id}/export?format=${fmt}`, "_blank");
  };

  const gauge = [{ name: "score", value: scores.overall, fill: "#0f172a" }];

  return (
    <div>
      <PageHeader
        overline={`${submission.tac_org || submission.tac_name} · v${submission.version}`}
        title={submission.title}
        description={`Immutable snapshot submitted ${fmtDate(submission.submitted_at)}.`}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate(-1)} className="rounded-md"><ArrowLeft className="h-4 w-4 mr-1.5" aria-hidden="true" /> Back</Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="rounded-md" data-testid="export-menu-button"><Download className="h-4 w-4 mr-1.5" aria-hidden="true" /> Export <ChevronDown className="h-3.5 w-3.5 ml-1" aria-hidden="true" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-md">
                <DropdownMenuItem onClick={() => download("pdf")} data-testid="export-pdf">PDF report</DropdownMenuItem>
                <DropdownMenuItem onClick={() => download("xlsx")} data-testid="export-xlsx">Excel (XLSX)</DropdownMenuItem>
                <DropdownMenuItem onClick={() => download("csv")} data-testid="export-csv">CSV</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-card border border-border rounded-md p-6 flex flex-col items-center justify-center">
          <div className="overline mb-2 self-start">Overall score</div>
          <div className="relative">
            <ResponsiveContainer width={180} height={180}>
              <RadialBarChart innerRadius="72%" outerRadius="100%" data={gauge} startAngle={90} endAngle={-270}>
                <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                <RadialBar dataKey="value" cornerRadius={4} background={{ fill: "#f1f5f9" }} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="font-heading text-4xl font-bold" data-testid="report-overall-score">{scores.overall}</div>
              <div className="text-xs text-muted-foreground">/ 100</div>
            </div>
          </div>
          <div className={`mt-3 font-heading font-bold ${bandColor(scores.band)}`} data-testid="report-band">{scores.band}</div>
        </div>

        <div className="lg:col-span-2 bg-card border border-border rounded-md p-6">
          <div className="overline mb-4">Pillar scores</div>
          <div className="space-y-4">
            {scores.pillars.map((p) => (
              <div key={p.pillar_name} data-testid={`pillar-score-${p.pillar_name.replace(/\s+/g, "-")}`}>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="font-medium">{p.pillar_name}</span>
                  <span className="font-mono font-semibold">{p.score}</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-slate-900 rounded-full transition-all" style={{ width: `${p.score}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {detail_level === "summary" ? (
        <div className="mt-6 flex items-center gap-2 rounded-md border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground" data-testid="summary-notice">
          <Lock className="h-4 w-4 shrink-0" aria-hidden="true" />
          You have summary access. Per-question breakdowns and validator adjustments are visible to EnterpriseSG reviewers (AM / PD).
        </div>
      ) : (
        <div className="mt-6 bg-card border border-border rounded-md overflow-hidden">
          <div className="px-5 py-3 border-b border-border overline">Detailed responses &amp; validation</div>
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="text-xs uppercase tracking-wider">Pillar</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Question</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-center">Self</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-center">Validator</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Validator comment</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {submission.responses.map((r) => {
                const vc = vcomments[r.question_id];
                return (
                  <TableRow key={r.question_id} className="border-b border-border/60">
                    <TableCell className="py-2.5 text-xs text-muted-foreground">{r.pillar_name}</TableCell>
                    <TableCell className="py-2.5 text-sm max-w-md">{r.question_text}<div className="text-xs text-muted-foreground mt-0.5 italic">{r.note}</div></TableCell>
                    <TableCell className="py-2.5 text-center font-mono">{r.score}/{r.max_score}</TableCell>
                    <TableCell className="py-2.5 text-center font-mono">{vc?.validator_score ?? "—"}</TableCell>
                    <TableCell className="py-2.5 text-sm text-muted-foreground">{vc?.comment || "—"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {validation?.overall_comment && (
            <div className="px-5 py-4 border-t border-border">
              <div className="overline mb-1">Overall validation comment</div>
              <p className="text-sm">{validation.overall_comment}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
